/**
 * Select Component - Constants
 * ============================
 */

import { getIcon } from '../../icons';

export type SelectSize = 'sm' | 'md';

export interface SelectOption {
  value: string;
  label: string;
  icon?: string;
  disabled?: boolean;
}

export interface SelectSizeStyles {
  trigger: string;
  text: string;
  option: string;
}

export interface SelectDefaultValues {
  size: SelectSize;
}

// Figma especifica un solo tamaño de trigger (h-48, px-4/py-2, chevron 16);
// `sm`/`md` se conservan como API — solo varía el tamaño de texto.
export const SIZES: Record<SelectSize, SelectSizeStyles> = {
  sm: { trigger: 'ec-px-4 ec-py-2', text: 'ec-text-base', option: 'ec-py-3 ec-text-sm' },
  md: { trigger: 'ec-px-4 ec-py-2', text: 'ec-text-base', option: 'ec-py-3 ec-text-base' }
};

export const BASE_CLASSES = {
  wrapper: 'ec-flex ec-flex-col ec-gap-1',
  relative: 'ec-relative',
  // Trigger (§4): h-48, borde gray/100 (#CCCCCC, ec-border-gray-light) — NO border/default.
  trigger:
    'ec-w-full ec-h-12 ec-flex ec-items-center ec-justify-between ec-gap-3 ec-bg-color-surface ' +
    'ec-border ec-border-gray-light ec-rounded-lg ec-transition-colors ec-cursor-pointer ' +
    'focus-visible:ec-outline-none focus-visible:ec-border-color-primary focus-visible:ec-ring-1 focus-visible:ec-ring-color-primary',
  // Error (patrón field §1): sin borde rojo — el texto del valor va en text-danger.
  triggerDisabled: 'ec-bg-color-bg ec-cursor-not-allowed',
  // Colores del texto del trigger: mutuamente excluyentes (nunca combinar dos).
  triggerText: 'ec-flex ec-items-center ec-gap-2 ec-truncate',
  triggerTextColor: 'ec-text-color-text',
  triggerTextError: 'ec-text-color-text-danger',
  triggerPlaceholder: 'ec-text-color-text-muted',
  chevron: 'ec-w-4 ec-h-4 ec-text-color-text-muted ec-transition-transform ec-flex-shrink-0',
  chevronOpen: 'ec-rotate-180',
  // Panel (§4): borde border/default, radio 8, SIN padding (lateral ni vertical) — el padding
  // vive en cada opción para que el hover/fondo de la opción llegue al borde del panel.
  panel:
    'ec-absolute ec-z-20 ec-left-0 ec-right-0 ec-mt-1 ec-bg-color-surface ec-border ec-border-color-border ' +
    'ec-rounded-lg ec-shadow-card ec-max-h-60 ec-overflow-auto ec-list-none ec-m-0',
  // Alineación de opción (§4): items-end (no center). px-4 lateral aquí (no en el panel).
  option: 'ec-flex ec-items-end ec-gap-2 ec-px-4 ec-cursor-pointer ec-text-color-text hover:ec-bg-color-bg ec-transition-colors',
  optionSelected: 'ec-bg-color-primary-light ec-text-color-primary ec-font-medium',
  optionDisabled: 'ec-opacity-50 ec-cursor-not-allowed hover:ec-bg-transparent',
  // Label/help calcados de InputText (patrón field §1).
  label: 'ec-text-base ec-text-color-text-secondary',
  help: 'ec-text-caption ec-font-medium',
  // Colores del help: mutuamente excluyentes.
  helpColor: 'ec-text-color-text-secondary',
  helpError: 'ec-text-color-text-danger'
};

/** Ícono del registro central (`src/icons`). */
export const CHEVRON_ICON: string = getIcon('caret-down') || '';

export const DEFAULTS: SelectDefaultValues = { size: 'md' };
